import React, { Suspense, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { PapiUser, PapiGallection } from '@wix/da-papi-types';
import TextButton from '@wix/da-ds/pkg/Buttons/TextButton';
import ArrowDown from '@wix/da-ds/pkg/Icons/ArrowDown';
import ArrowUp from '@wix/da-ds/pkg/Icons/ArrowUp';

import ErrorBoundary from '@wix/da-react-context/pkg/ErrorBoundary';
import LoadingIndicator from '@wix/da-ds/pkg/LoadingIndicator';
import ModuleLoaderLazy from './redux/ModuleLoaderLazy';

import GroupGallectionPopover from './GroupGallectionPopover';

import s from './GroupGallectionSelector.scss';

export interface Props {
  className?: string;
  gallectionType: 'gallery' | 'collection';
  group: PapiUser;
  selected?: PapiGallection;
  onSelect?: (gallection: PapiGallection) => void;
}

const GroupGallectionSelector: React.FC<Props> = ({
  className,
  gallectionType,
  group,
  selected,
  onSelect,
}) => {
  const { t } = useTranslation();
  const [showPopover, setShowPopover] = useState(false);
  const hidePopover = useCallback(() => setShowPopover(false), []);
  const togglePopover = useCallback(() => setShowPopover(!showPopover), [showPopover]);

  return (
    <ErrorBoundary debugComponent="ModalLoadable">
      <Suspense fallback={<LoadingIndicator />}>
        <ModuleLoaderLazy />
        <div className={className}>
          <span className={s['toggle']}>
            {t('groupGallectionSelector.popover.toggle.addToGroup')}
          </span>
          <TextButton
            onClick={togglePopover}
            size="small"
            variant="main"
            className={s['btn-toggle']}
            postfix={showPopover ? ArrowUp : ArrowDown}
          >
            {selected?.name || 'Featured'}
          </TextButton>
          <GroupGallectionPopover
            group={group}
            isVisible={showPopover}
            gallectionType={gallectionType}
            requestClose={hidePopover}
            selected={selected}
            onSelect={onSelect}
          />
        </div>
      </Suspense>
    </ErrorBoundary>
  );
};
GroupGallectionSelector.displayName = 'GroupGallectionSelector';

export default GroupGallectionSelector;
